// ─────────────────────────────────────────────────────────────────────────────
// useNotes — GET /api/rexy/notes. Shared core (owner ruling 2026-07-31).
//
// NOTES — ONE 1-2 SENTENCE REFLECTION PER EVENT (§5C), grouped under the pulse
// hour. Each event she reads gets its OWN substantive note — not one collapsed
// gloss per pulse. `content` is the verbatim Tier-2 artifact text; EVERY surface
// mirrors it exactly and never reshapes it.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState } from 'react';
import { authHeaders, POLL_MS } from './authHeaders';
import { makeTimeFormatter } from './timeFormat';
import { NOTES } from './endpoints';

// One event's note — her genuine 1-2 sentence reflection on THAT event.
export type NoteEntry = {
  note_id: string;
  ts: string;
  who: string | null;       // the source event's WHO (agent/domain) — display label
  where: string | null;     // the source event's WHERE — display label
  subject: string | null;   // the event's subject
  source_kind: string | null;
  content: string;          // her reflection on this event, VERBATIM
};

export type NoteHour = {
  slot_id: string;
  scheduled_for: string;
  // 'written' all events noted; 'partial' some failed; 'empty' quiet hour; 'failed'
  // had events but none noted; 'missing' no pulse; 'unreadable' store failure.
  status: 'written' | 'partial' | 'empty' | 'failed' | 'unreadable' | 'missing';
  notes: NoteEntry[];       // one per event, newest-first, each its own 1-2 sentences
  failed?: number;
  error?: string | null;
};

export type NotesView = {
  ledger_status: 'ok' | 'fresh' | 'unreadable';
  timezone: string;
  hours: NoteHour[];
};
export type NotesResponse = { success: boolean; notes: NotesView; error?: string };

export const NOTE_KIND_LABEL: Record<string, string> = {
  agent_dashboard: 'dashboard',
  domain_board: 'board',
  chat: 'chat',
};

export type NotesFeed = {
  notes: NotesView | null;
  err: string | null;
  updatedAt: string | null;
  hourLabel: (iso: string) => string;
  stampLabel: (iso: string) => string;
};

export function useNotes(): NotesFeed {
  const [notes, setNotes] = useState<NotesView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const hourLabel = useMemo(() => makeTimeFormatter(notes?.timezone), [notes?.timezone]);
  const stampLabel = useMemo(() => makeTimeFormatter(notes?.timezone, true), [notes?.timezone]);

  useEffect(() => {
    const fetchNotes = () => {
      fetch(NOTES, { headers: authHeaders() })
        .then((r) => r.json())
        .then((j) => {
          if (j && j.success && j.notes) {
            setNotes((j as NotesResponse).notes);
            setErr(null);
            setUpdatedAt(new Date().toISOString());
          } else setErr((j && j.error) || 'unavailable');
        })
        .catch((e) => setErr(String(e)));
    };
    fetchNotes();
    const poll = setInterval(fetchNotes, POLL_MS);
    return () => clearInterval(poll);
  }, []);

  return { notes, err, updatedAt, hourLabel, stampLabel };
}
